import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  reviews: [],
  tempReviews: [],
  reviewStats: null,
  customerReview: null,
  pendingReviews: [],
  allReviews: [],
  reviewSettings: null,
  isLoading: false,
  statsLoading: false,
  error: null,
  pagination: {
    total: 0,
    page: 1,
    pages: 1,
    limit: 5,
  },
  pendingPagination: {
    total: 0,
    page: 1,
    pages: 1,
    limit: 20,
  },
  allReviewsPagination: {
    total: 0,
    page: 1,
    pages: 1,
    limit: 20,
  },
  imagePopup: {
    isOpen: false,
    images: [],
    currentIndex: 0,
  },
};

const reviewSlice = createSlice({
  name: "reviews",
  initialState,
  reducers: {
    // Review list actions
    setReviews(state, action) {
      state.reviews = action.payload;
      state.isLoading = false;
      state.error = null;
    },
    setTempReviews(state, action) {
      state.tempReviews = action.payload;
    },
    setReviewPagination(state, action) {
      state.pagination = action.payload;
    },
    
    // Review stats actions
    setReviewStats(state, action) {
      state.reviewStats = action.payload;
      state.statsLoading = false;
      state.error = null;
    },
    
    // Customer review actions
    setCustomerReview(state, action) {
      state.customerReview = action.payload;
    },
    
    // Pending reviews actions
    setPendingReviews(state, action) {
      state.pendingReviews = action.payload;
      state.isLoading = false;
    },
    setPendingPagination(state, action) {
      state.pendingPagination = action.payload;
    },
    
    // All reviews (admin) actions
    setAllReviews(state, action) {
      state.allReviews = action.payload;
      state.isLoading = false;
    },
    setAllReviewsPagination(state, action) {
      state.allReviewsPagination = action.payload;
    },
    
    // Review settings actions
    setReviewSettings(state, action) {
      state.reviewSettings = action.payload;
      state.isLoading = false;
    },
    
    // Loading states
    setReviewIsLoading(state, action) {
      state.isLoading = true;
      state.error = null;
    },
    setStatsLoading(state, action) {
      state.statsLoading = action.payload !== undefined ? action.payload : true;
    },
    
    // Error
    setReviewError(state, action) {
      state.error = action.payload;
      state.isLoading = false;
      state.statsLoading = false;
    },
    
    // Image popup actions
    openImagePopup(state, action) {
      const { images, startIndex = 0 } = action.payload;
      state.imagePopup = {
        isOpen: true,
        images: images,
        currentIndex: startIndex,
      };
    },
    closeImagePopup(state) {
      state.imagePopup = {
        isOpen: false,
        images: [],
        currentIndex: 0,
      };
    },
    setImageIndex(state, action) {
      state.imagePopup.currentIndex = action.payload;
    },
    nextImage(state) {
      const { currentIndex, images } = state.imagePopup;
      state.imagePopup.currentIndex = (currentIndex + 1) % images.length;
    },
    prevImage(state) {
      const { currentIndex, images } = state.imagePopup;
      state.imagePopup.currentIndex = (currentIndex - 1 + images.length) % images.length;
    },
    
    // CRUD operations
    addReview(state, action) {
      state.reviews.unshift(action.payload);
      state.tempReviews.unshift(action.payload);
      
      // Update stats if needed
      if (state.reviewStats) {
        state.reviewStats.totalReviews += 1;
        const rating = Math.round(action.payload.rating);
        state.reviewStats.ratingDistribution[rating] = 
          (state.reviewStats.ratingDistribution[rating] || 0) + 1;
        state.reviewStats.averageRating = 
          (state.reviewStats.averageRating * (state.reviewStats.totalReviews - 1) + action.payload.rating) 
          / state.reviewStats.totalReviews;
      }
    },
    
    updateReview(state, action) {
      const { index, review } = action.payload;
      state.reviews?.splice(index, 1, review);
      state.tempReviews?.splice(index, 1, review);
      
      // Update in allReviews if present
      const allIndex = state.allReviews.findIndex(r => r._id === review._id);
      if (allIndex !== -1) {
        state.allReviews.splice(allIndex, 1, review);
      }
    },
    
    deleteReview(state, action) {
      const { index, reviewId } = action.payload;
      
      if (index !== undefined) {
        state.reviews.splice(index, 1);
        state.tempReviews.splice(index, 1);
      } else if (reviewId) {
        state.reviews = state.reviews.filter(r => r._id !== reviewId);
        state.tempReviews = state.tempReviews.filter(r => r._id !== reviewId);
      }
      
      // Remove from allReviews if present
      state.allReviews = state.allReviews.filter(r => r._id !== (reviewId || action.payload.reviewId));
    },
    
    // Like/Unlike review
    toggleReviewLike(state, action) {
      const { reviewId, isLiked, likesCount } = action.payload;
      
      // Update in reviews list
      state.reviews = state.reviews.map(review => {
        if (review._id === reviewId) {
          return {
            ...review,
            isLiked: isLiked !== undefined ? isLiked : !review.isLiked,
            likesCount: likesCount !== undefined ? likesCount : 
              (review.isLiked ? review.likesCount - 1 : review.likesCount + 1)
          };
        }
        return review;
      });
      
      // Update in tempReviews
      state.tempReviews = state.tempReviews.map(review => {
        if (review._id === reviewId) {
          return {
            ...review,
            isLiked: isLiked !== undefined ? isLiked : !review.isLiked,
            likesCount: likesCount !== undefined ? likesCount : 
              (review.isLiked ? review.likesCount - 1 : review.likesCount + 1)
          };
        }
        return review;
      });
      
      // Update in customerReview if it's the customer's own review
      if (state.customerReview && state.customerReview._id === reviewId) {
        state.customerReview = {
          ...state.customerReview,
          isLiked: isLiked !== undefined ? isLiked : !state.customerReview.isLiked,
          likesCount: likesCount !== undefined ? likesCount : 
            (state.customerReview.isLiked ? state.customerReview.likesCount - 1 : state.customerReview.likesCount + 1)
        };
      }
    },
    
    // Approve/Reject reviews
    approveReview(state, action) {
      const { reviewId, review } = action.payload;
      
      // Update in pending reviews
      state.pendingReviews = state.pendingReviews.filter(r => r._id !== reviewId);
      
      // Update stats if needed
      if (review && state.reviewStats) {
        state.reviewStats.totalReviews += 1;
        const rating = Math.round(review.rating);
        state.reviewStats.ratingDistribution[rating] = 
          (state.reviewStats.ratingDistribution[rating] || 0) + 1;
        state.reviewStats.averageRating = 
          (state.reviewStats.averageRating * (state.reviewStats.totalReviews - 1) + review.rating) 
          / state.reviewStats.totalReviews;
        
        if (review.isVerifiedPurchase) {
          state.reviewStats.verifiedReviews += 1;
        }
        if (review.photos?.length > 0) {
          state.reviewStats.withPhotos += 1;
        }
      }
    },
    
    rejectReview(state, action) {
      const { reviewId } = action.payload;
      state.pendingReviews = state.pendingReviews.filter(r => r._id !== reviewId);
    },
    
    // Bulk actions
    bulkApproveReviews(state, action) {
      const { reviewIds } = action.payload;
      state.pendingReviews = state.pendingReviews.filter(r => !reviewIds.includes(r._id));
    },
    
    bulkRejectReviews(state, action) {
      const { reviewIds } = action.payload;
      state.pendingReviews = state.pendingReviews.filter(r => !reviewIds.includes(r._id));
    },
    
    // Reset
    resetReviewState(state) {
      return initialState;
    },
  },
});

export const {
  setReviews,
  setTempReviews,
  setReviewPagination,
  setReviewStats,
  setCustomerReview,
  setPendingReviews,
  setPendingPagination,
  setAllReviews,
  setAllReviewsPagination,
  setReviewSettings,
  setReviewIsLoading,
  setStatsLoading,
  setReviewError,
  openImagePopup,
  closeImagePopup,
  setImageIndex,
  nextImage,
  prevImage,
  addReview,
  updateReview,
  deleteReview,
  toggleReviewLike,
  approveReview,
  rejectReview,
  bulkApproveReviews,
  bulkRejectReviews,
  resetReviewState,
} = reviewSlice.actions;

export default reviewSlice.reducer;